import React, { useEffect, useState, useMemo } from "react";
import { Chart } from "react-google-charts";
import "../styles/TotalPendapatan.css";

const API_BASE_URL = process.env.REACT_APP_API_URL || "https://depotaircerdas.com/api";

const formatCurrency = (value) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const TotalPendapatan = () => {
  const username = localStorage.getItem("username");
  const token = localStorage.getItem("token");

  const [data, setData] = useState([]);
  const [salesTotal, setSalesTotal] = useState(0);
  const [hargaPerGalon, setHargaPerGalon] = useState(6000);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!username || !token) {
      window.location.href = "/login";
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError("");

        const [pendapatanRes, salesRes] = await Promise.all([
          fetch(`${API_BASE_URL}/pendapatan/${username}`, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }),
          fetch(`${API_BASE_URL}/sales/total/${username}`, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }),
        ]);

        if (!pendapatanRes.ok) {
          throw new Error("Gagal mengambil data pendapatan.");
        }

        if (!salesRes.ok) {
          throw new Error("Gagal mengambil total penjualan.");
        }

        const pendapatanJson = await pendapatanRes.json();
        const salesJson = await salesRes.json();

        setData(Array.isArray(pendapatanJson?.data) ? pendapatanJson.data : []);
        setSalesTotal(Number(salesJson?.total_pendapatan || 0));
      } catch (err) {
        console.error("❌ Error fetching pendapatan:", err);
        setError(err.message || "Gagal mengambil data pendapatan.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [username, token]);

  const totals = useMemo(() => {
    const gallonBased = Array.isArray(data)
      ? data.reduce((sum, d) => sum + Number(d.gallons || 0) * hargaPerGalon, 0)
      : 0;

    const totalGallons = Array.isArray(data)
      ? data.reduce((sum, d) => sum + Number(d.gallons || 0), 0)
      : 0;

    return {
      gallonBased,
      totalGallons,
      totalPeriods: Array.isArray(data) ? data.length : 0,
    };
  }, [data, hargaPerGalon]);

  return (
    <div className="page-shell">
      <div className="page-hero-card">
        <span className="page-hero-badge">Analisis Pendapatan</span>
        <h2 className="page-hero-title">Total Pendapatan</h2>
        <p className="page-hero-subtitle">
          Pantau estimasi pendapatan berdasarkan jumlah galon terjual dan bandingkan
          dengan nilai penjualan yang tercatat pada sistem.
        </p>
      </div>

      <div className="page-card">
        <div className="page-card-header">
          <div>
            <h3 className="section-title">Ringkasan Pendapatan</h3>
            <p className="section-subtitle">
              Tinjauan cepat total galon, estimasi pendapatan, dan penjualan aktual.
            </p>
          </div>
        </div>

        <div className="pendapatan-summary-grid">
          <div className="pendapatan-stat-card">
            <span>Estimasi dari Galon</span>
            <strong>{formatCurrency(totals.gallonBased)}</strong>
            <small>Berdasarkan harga per galon saat ini</small>
          </div>

          <div className="pendapatan-stat-card">
            <span>Penjualan Tercatat</span>
            <strong>{formatCurrency(salesTotal)}</strong>
            <small>Total dari transaksi sales</small>
          </div>

          <div className="pendapatan-stat-card">
            <span>Total Galon</span>
            <strong>{totals.totalGallons}</strong>
            <small>Akumulasi seluruh periode</small>
          </div>

          <div className="pendapatan-stat-card">
            <span>Jumlah Periode</span>
            <strong>{totals.totalPeriods}</strong>
            <small>Jumlah data yang tercatat</small>
          </div>
        </div>

        <div className="pendapatan-input-card">
          <label htmlFor="hargaPerGalon">Harga per Galon (Rp)</label>
          <input
            id="hargaPerGalon"
            type="number"
            value={hargaPerGalon}
            onChange={(e) => setHargaPerGalon(Number(e.target.value))}
          />
        </div>
      </div>

      {loading ? (
        <div className="state-box">Memuat data pendapatan...</div>
      ) : error ? (
        <div className="state-box error">{error}</div>
      ) : data.length === 0 ? (
        <div className="state-box">Belum ada data pendapatan yang tersedia.</div>
      ) : (
        <>
          <div className="page-card">
            <div className="page-card-header">
              <div>
                <h3 className="section-title">Grafik Pendapatan per Periode</h3>
                <p className="section-subtitle">
                  Perbandingan nilai pendapatan dari galon yang terjual.
                </p>
              </div>
            </div>

            <div className="pendapatan-chart-wrap">
              <Chart
                chartType="ColumnChart"
                width="100%"
                height="380px"
                data={[
                  ["Periode", "Pendapatan"],
                  ...data.map((d) => [
                    String(d.periode).split("T")[0],
                    Number(d.gallons || 0) * hargaPerGalon,
                  ]),
                ]}
                options={{
                  title: "Pendapatan per Periode",
                  legend: { position: "top" },
                  hAxis: { title: "Periode" },
                  vAxis: {
                    title: "Pendapatan (IDR)",
                    gridlines: { count: 5 },
                  },
                  chartArea: {
                    left: 70,
                    top: 60,
                    width: "82%",
                    height: "68%",
                  },
                }}
              />
            </div>
          </div>

          <div className="page-card">
            <div className="page-card-header">
              <div>
                <h3 className="section-title">Tabel Pendapatan</h3>
                <p className="section-subtitle">
                  Detail jumlah galon dan estimasi pendapatan per periode.
                </p>
              </div>
            </div>

            <div className="pendapatan-table-wrap">
              <table className="pendapatan-table-modern">
                <thead>
                  <tr>
                    <th>Periode</th>
                    <th>Jumlah Galon</th>
                    <th>Pendapatan (Rp)</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((item, index) => (
                    <tr key={index}>
                      <td>{String(item.periode).split("T")[0]}</td>
                      <td>{item.gallons}</td>
                      <td>{formatCurrency(Number(item.gallons || 0) * hargaPerGalon)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TotalPendapatan;